(() => {
  if (window.__solviCustomSwymPage) return;
  window.__solviCustomSwymPage = true;

  const ROOT_SELECTOR = '[data-solvi-swym-page]';
  const READY_EVENT = 'solvi:swym-ready';

  const productCache = new Map();

  let swym = null;
  let wishlistItems = [];
  let requestId = 0;


  const escapeHtml = (value) =>
    String(value == null ? '' : value).replace(
      /[&<>"']/g,
      (character) =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;'
        })[character]
    );


  const normalizeWishlistItems = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.items)) return response.items;
    if (Array.isArray(response?.products)) return response.products;
    if (Array.isArray(response?.wishlistItems)) return response.wishlistItems;
    if (Array.isArray(response?.data?.items)) return response.data.items;

    return [];
  };


  const wishlistKey = (item) =>
    `${String(item?.empi || '')}:${String(item?.epi || '')}`;


  const uniqueItems = (items) => {
    const unique = new Map();

    items.forEach((item) => {
      const key = wishlistKey(item);

      if (key !== ':') {
        unique.set(key, item);
      }
    });

    return Array.from(unique.values());
  };


  const getRoot = () => document.querySelector(ROOT_SELECTOR);


  const getElements = () => {
    const root = getRoot();

    if (!root) return null;

    return {
      root,
      loading: root.querySelector('[data-solvi-swym-loading]'),
      empty: root.querySelector('[data-solvi-swym-empty]'),
      grid: root.querySelector('[data-solvi-swym-grid]'),
      error: root.querySelector('[data-solvi-swym-error]'),
      count: root.querySelector('[data-solvi-swym-count]'),
      status: root.querySelector('[data-solvi-swym-status]'),
      retry: root.querySelector('[data-solvi-swym-retry]')
    };
  };


  const setState = (state) => {
    const elements = getElements();

    if (!elements) return;

    elements.loading.hidden = state !== 'loading';
    elements.empty.hidden = state !== 'empty';
    elements.grid.hidden = state !== 'grid';
    elements.error.hidden = state !== 'error';

    if (state !== 'grid') {
      elements.count.hidden = true;
    }
  };


  const announce = (message) => {
    const elements = getElements();

    if (elements?.status) {
      elements.status.textContent = message;
    }
  };


  const formatMoney = (cents) => {
    const root = getRoot();
    const currency = root?.dataset.currency || 'USD';
    const amount = Number(cents);

    if (!Number.isFinite(amount)) return '';

    try {
      return new Intl.NumberFormat(
        document.documentElement.lang || 'en-US',
        {
          style: 'currency',
          currency,
          maximumFractionDigits: 2
        }
      ).format(amount / 100);
    } catch (_error) {
      return `$${(amount / 100).toFixed(2)}`;
    }
  };


  const productJsonUrl = (item) => {
    const rawUrl =
      item?.du ||
      item?.url ||
      item?.productUrl ||
      '';

    if (!rawUrl) return '';

    try {
      const url = new URL(rawUrl, window.location.origin);

      let path = url.pathname.replace(/\/+$/, '');

      if (!path.endsWith('.js')) {
        path += '.js';
      }

      return `${path}${url.search || ''}`;
    } catch (_error) {
      return '';
    }
  };


  const fetchProduct = async (item) => {
    const key = String(item?.empi || item?.du || '');

    if (productCache.has(key)) {
      return productCache.get(key);
    }

    const promise = (async () => {
      const url = productJsonUrl(item);

      if (!url) {
        return {
          wishlistItem: item,
          product: null,
          variant: null
        };
      }

      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store'
        });

        if (!response.ok) {
          throw new Error(`Product request returned ${response.status}`);
        }

        const product = await response.json();

        const variantId = String(item?.epi || '');

        const variant =
          product.variants?.find(
            (candidate) => String(candidate.id) === variantId
          ) ||
          product.variants?.find((candidate) => candidate.available) ||
          product.variants?.[0] ||
          null;

        return {
          wishlistItem: item,
          product,
          variant
        };
      } catch (_error) {
        return {
          wishlistItem: item,
          product: null,
          variant: null
        };
      }
    })();

    productCache.set(key, promise);

    return promise;
  };


  const fallbackProductUrl = (item) => {
    if (!item?.du) return '#';

    try {
      return new URL(item.du, window.location.origin).href;
    } catch (_error) {
      return '#';
    }
  };


  const cardMarkup = ({ wishlistItem, product, variant }) => {
    const key = wishlistKey(wishlistItem);

    const productUrl =
      product?.url ||
      fallbackProductUrl(wishlistItem);

    const title =
      product?.title ||
      wishlistItem?.dt ||
      'Saved scent';

    const vendor =
      product?.vendor ||
      '';

    const image =
      product?.featured_image ||
      product?.images?.[0] ||
      wishlistItem?.iu ||
      '';

    const price =
      variant?.price != null
        ? formatMoney(variant.price)
        : product?.price != null
          ? formatMoney(product.price)
          : '';

    const variantTitle =
      variant?.title &&
      variant.title !== 'Default Title'
        ? variant.title
        : '';

    return `
      <article class="solvi-shelf-card" data-solvi-shelf-item="${escapeHtml(key)}">

        <div class="solvi-shelf-card__media">

          <a
            class="solvi-shelf-card__image-link"
            href="${escapeHtml(productUrl)}"
            aria-label="View ${escapeHtml(title)}"
          >
            ${
              image
                ? `
                  <img
                    class="solvi-shelf-card__image"
                    src="${escapeHtml(image)}"
                    alt="${escapeHtml(title)}"
                    loading="lazy"
                  >
                `
                : `
                  <span class="solvi-shelf-card__placeholder" aria-hidden="true">
                    ♡
                  </span>
                `
            }
          </a>

          <button
            class="solvi-shelf-card__remove"
            type="button"
            data-solvi-swym-remove="${escapeHtml(key)}"
            aria-label="Remove ${escapeHtml(title)} from My Scent Shelf"
            title="Remove from shelf"
          >
            <svg
              viewBox="0 0 24 24"
              width="22"
              height="22"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6.5 3.75h11v16.5L12 16.7l-5.5 3.55V3.75Z"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />
            </svg>
          </button>

        </div>


        <div class="solvi-shelf-card__content">

          ${
            vendor
              ? `<p class="solvi-shelf-card__vendor">${escapeHtml(vendor)}</p>`
              : ''
          }

          <h2 class="solvi-shelf-card__title">
            <a href="${escapeHtml(productUrl)}">
              ${escapeHtml(title)}
            </a>
          </h2>

          <div class="solvi-shelf-card__meta">
            ${
              price
                ? `<span class="solvi-shelf-card__price">${escapeHtml(price)}</span>`
                : ''
            }

            ${
              variantTitle
                ? `<span class="solvi-shelf-card__variant">${escapeHtml(variantTitle)}</span>`
                : ''
            }
          </div>

          <a
            class="solvi-shelf-card__view"
            href="${escapeHtml(productUrl)}"
          >
            VIEW SCENT →
          </a>

        </div>
      </article>
    `;
  };


  const renderGrid = async (items, currentRequestId) => {
    const elements = getElements();

    if (!elements) return;

    const hydrated = await Promise.all(
      items.map(fetchProduct)
    );

    if (currentRequestId !== requestId) return;

    elements.grid.innerHTML = hydrated
      .map(cardMarkup)
      .join('');

    elements.count.textContent =
      items.length === 1
        ? '1 scent on your shelf'
        : `${items.length} scents on your shelf`;

    elements.count.hidden = false;

    setState('grid');

    announce(
      `${items.length} ${
        items.length === 1 ? 'scent' : 'scents'
      } loaded on your shelf.`
    );
  };


  const renderResponse = async (response, currentRequestId) => {
    if (currentRequestId !== requestId) return;

    wishlistItems = uniqueItems(
      normalizeWishlistItems(response)
    );

    if (!wishlistItems.length) {
      setState('empty');
      announce('Your Scent Shelf is empty.');
      return;
    }

    await renderGrid(
      wishlistItems,
      currentRequestId
    );
  };


  const loadShelf = () => {
    if (!swym || typeof swym.fetch !== 'function') {
      return;
    }

    const currentRequestId = ++requestId;

    setState('loading');

    try {
      swym.fetch(
        (response) => {
          renderResponse(
            response,
            currentRequestId
          );
        },
        () => {
          if (currentRequestId !== requestId) return;

          setState('error');

          announce(
            'We could not load your Scent Shelf.'
          );
        }
      );
    } catch (_error) {
      if (currentRequestId !== requestId) return;

      setState('error');

      announce(
        'We could not load your Scent Shelf.'
      );
    }
  };


  const removeItem = (key, button) => {
    if (
      !swym ||
      typeof swym.removeFromWishList !== 'function'
    ) {
      return;
    }

    const item = wishlistItems.find(
      (candidate) => wishlistKey(candidate) === key
    );

    if (!item) return;

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    try {
      swym.removeFromWishList(
        item,
        () => {
          wishlistItems = wishlistItems.filter(
            (candidate) =>
              wishlistKey(candidate) !== key
          );

          productCache.delete(
            String(item?.empi || item?.du || '')
          );

          const currentRequestId = ++requestId;

          if (!wishlistItems.length) {
            setState('empty');
            announce('Removed from your Scent Shelf.');
            return;
          }

          renderGrid(
            wishlistItems,
            currentRequestId
          );

          announce('Removed from your Scent Shelf.');
        },
        () => {
          if (button) {
            button.disabled = false;
            button.removeAttribute('aria-busy');
          }

          announce(
            'We could not remove that scent. Please try again.'
          );
        }
      );
    } catch (_error) {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }

      announce(
        'We could not remove that scent. Please try again.'
      );
    }
  };


  const attachEvents = () => {
    const elements = getElements();

    if (!elements) return;

    elements.grid.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest(
          '[data-solvi-swym-remove]'
        );

        if (!button) return;

        event.preventDefault();

        removeItem(
          button.dataset.solviSwymRemove,
          button
        );
      }
    );

    elements.retry?.addEventListener(
      'click',
      () => loadShelf()
    );
  };


  const acceptSwym = (candidate) => {
    if (
      !candidate ||
      typeof candidate.fetch !== 'function'
    ) {
      return;
    }

    if (swym === candidate) return;

    swym = candidate;

    loadShelf();
  };


  const init = () => {
    const root = getRoot();

    if (!root) return;

    attachEvents();

    setState('loading');


    /* Custom Solvi Swym-ready bridge */

    window.addEventListener(
      READY_EVENT,
      (event) => {
        acceptSwym(event.detail?.swat);
      }
    );


    /* Native Swym callback */

    window.SwymCallbacks =
      window.SwymCallbacks || [];

    window.SwymCallbacks.push((swat) => {
      acceptSwym(swat);
    });


    /* SDK may already exist */

    acceptSwym(
      window.solviSwymSdk ||
      window._swat
    );


    /*
      If Swym never initializes, show a proper message instead
      of leaving the shopper staring at a loading state forever.
    */

    window.setTimeout(() => {
      if (swym) return;

      setState('error');

      announce(
        'We could not connect to your Scent Shelf.'
      );
    }, 10000);
  };


  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      init,
      { once: true }
    );
  } else {
    init();
  }
})();